import type { ReactNode } from "react";

import { ExploreThisLink } from "@/components/dashboard/explore-this-link";
import { useTranslation } from "@/i18n/use-translation";

type Props = {
  title: string;
  description?: string;
  /** Optional control (e.g. a metric toggle) rendered top-right. */
  action?: ReactNode;
  /** "Explore this" target for the panel (POS2-886); null/omitted renders nothing. Sits at
   * the bottom-right, the same corner on every panel — see `ExploreThisLink`. */
  exploreHref?: string | null;
  /** When true, shows the empty label instead of the chart. */
  isEmpty: boolean;
  emptyLabel?: string;
  children: ReactNode;
};

/** Panel chrome shared by the dashboard charts — homepage panel tokens (POS2-865):
 *  radius 12, hairline border, soft shadow, display-font title. */
export function ChartCard({
  title,
  description,
  action,
  exploreHref,
  isEmpty,
  emptyLabel,
  children,
}: Props) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-gray-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(17,24,39,0.04),0_1px_3px_rgba(17,24,39,0.06)]">
      {/* Wraps: the title and the action share one line until they cannot, then the
          action drops below (POS2-1072). Without it a card with an action overflows at
          390px, where the whole card is 260px wide. */}
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div>
          <h2 className="font-display text-sm font-bold tracking-tight text-gray-900">{title}</h2>
          {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
        </div>
        {action}
      </div>
      {isEmpty ? (
        <div className="flex h-[240px] items-center justify-center text-sm text-gray-400">
          {emptyLabel ?? t("waste.noDataForPeriod")}
        </div>
      ) : (
        <div className="mt-4">{children}</div>
      )}
      <ExploreThisLink href={exploreHref ?? null} label={title} />
    </div>
  );
}
